import type { PendingConflict } from "@/stores/upload-store";
import { useEffect, useId, useRef } from "react";

interface ConflictDialogProps {
	conflict: PendingConflict;
	onReplace: () => void;
	onCancel: () => void;
}

/**
 * Модальный диалог конфликта rate_book при загрузке файла.
 * Показывается когда в системе уже есть книга расценок и пользователь пытается загрузить новую.
 */
export function ConflictDialog({ conflict, onReplace, onCancel }: ConflictDialogProps) {
	const titleId = useId();
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (el && !el.open) el.showModal();
	}, []);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		const handler = (e: Event) => {
			e.preventDefault();
			onCancel();
		};
		el.addEventListener("cancel", handler);
		return () => el.removeEventListener("cancel", handler);
	}, [onCancel]);

	return (
		<dialog
			ref={dialogRef}
			className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
			aria-labelledby={titleId}
		>
			<h3 id={titleId} className="mb-1 text-lg font-semibold text-slate-900">
				Книга расценок уже загружена
			</h3>

			<div className="mb-4 text-sm text-slate-500">
				<p className="mb-2">
					В системе уже есть книга расценок{" "}
					<span className="font-medium text-slate-700">{conflict.existingFile.originalName}</span>.
				</p>
				<p className="text-amber-600">
					Может быть только одна книга расценок. При замене все расценки будут удалены.
				</p>
			</div>

			<div className="flex justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
				>
					Отменить
				</button>
				<button
					type="button"
					onClick={onReplace}
					className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
				>
					Заменить
				</button>
			</div>
		</dialog>
	);
}
